import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import { articleImageUrls } from '@/mocks/imageUrls';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-heading text-foreground-950 mb-2">文章未找到</h2>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: articleImageUrls[article.slug] || '',
    publisher: {
      '@type': 'Organization',
      name: '成人视频指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://example.com/article/${slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen pt-16">
        <article>
          {/* Header */}
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
                    { label: article.title },
                  ]}
                />
              </div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                <span>{article.author}</span>
                <span className="text-foreground-400">·</span>
                <span>{article.publishDate}</span>
                <span className="text-foreground-400">·</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="aspect-[16/9] rounded-lg overflow-hidden">
              <img
                src={articleImageUrls[article.slug] || 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20Japanese%20media%20content%20guide%20on%20a%20clean%20desk%20with%20soft%20warm%20lighting%2C%20editorial%20photography%20style&width=1200&height=675&seq=article-default&orientation=landscape'}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 pb-12">
            <div
              className="prose-content text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 rounded-full bg-background-200/70 text-foreground-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => {
                  const relCategory = categories.find((c) => c.id === rel.categoryId);
                  const relImageUrl = articleImageUrls[rel.slug] || 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20Japanese%20media%20guide%20on%20a%20clean%20desk%20with%20soft%20warm%20lighting%2C%20editorial%20photography%20style&width=800&height=500&seq=default&orientation=landscape';
                  return (
                    <article key={rel.id} className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden group hover:border-background-300/60 transition-all duration-300">
                      <Link to={`/article/${rel.slug}`} className="block">
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={relImageUrl}
                            alt={rel.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      </Link>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          {relCategory && (
                            <Link
                              to={`/category/${relCategory.slug}`}
                              className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer"
                            >
                              {relCategory.name}
                            </Link>
                          )}
                          <span className="text-xs text-foreground-500">{rel.publishDate}</span>
                        </div>
                        <Link to={`/article/${rel.slug}`}>
                          <h3 className="text-base font-semibold font-heading text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {rel.title}
                          </h3>
                        </Link>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {rel.excerpt}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </div>

      <style>{`
        .prose-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          font-family: var(--font-heading);
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .prose-content p {
          margin-bottom: 1.25rem;
          line-height: 1.85;
          font-size: 0.95rem;
          color: oklch(var(--foreground-700));
        }
        .prose-content strong {
          color: oklch(var(--foreground-900));
          font-weight: 600;
        }
        .prose-content p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </>
  );
}